import { SERVICE, ServiceType } from '@src/api/types/shared/serviceType';
import SpinnerOverlay from '@src/components/SpinnerOverlay';
import { SESSION_STORAGE_KEYS } from '@src/constants/sessionStorage';
import { useCompanyAuth } from '@src/context/auth/useCompanyAuth';
import { CompanyRoleType } from '@src/enum/companyRoleEnum';
import Error404 from '@src/pages/Error404';
import { COMPANY_URL } from '@src/routes/company';
import { isValidDxstarUrl } from '@src/utils/redirectAfterLogin';
import React from 'react';
import { useNavigate } from 'react-router-dom';

type Props = {
  children: React.ReactNode;
  canRoles?: CompanyRoleType[];
  canService?: ServiceType[];
};

const CompanyGuard: React.FC<Props> = ({ children, canRoles, canService }) => {
  const { profile } = useCompanyAuth();
  const navigate = useNavigate();

  if (!profile) {
    return <SpinnerOverlay loading={true} />;
  }

  if (canRoles) {
    if (!profile.isLogin) {
      if (window.location.pathname === COMPANY_URL.LOGIN) {
        return null;
      }

      const currentUrl = window.location.pathname + window.location.search;

      if (!isValidDxstarUrl(currentUrl)) {
        console.error('Invalid redirect URL');
        return null;
      }

      sessionStorage.setItem(SESSION_STORAGE_KEYS.REDIRECT_AFTER_LOGIN, currentUrl);
      navigate(`${COMPANY_URL.LOGIN}?redirect=${encodeURIComponent(currentUrl)}`);
      return null;
    }

    if (!profile.user) {
      return <SpinnerOverlay loading={true} />;
    }

    const hasRequiredRole = canRoles.some((role) => profile?.user?.role.code === role);
    if (!hasRequiredRole || !profile?.user?.enabled) {
      return <Error404 isTenant={false} />;
    }
  }

  if (canService) {
    if (!profile.tenant) {
      return <SpinnerOverlay loading={true} />;
    }

    const hasRequiredService = canService.some((service) => {
      if (service === SERVICE.ENTERPRISE_PORTAL) {
        return profile?.tenant?.service?.enterprise_portal;
      }
      if (service === SERVICE.DX_DIAGNOSIS) {
        return profile?.tenant?.service?.dx_diagnosis;
      }
      if (service === SERVICE.ONLINE_COMMUNITY) {
        return profile?.tenant?.service?.online_community;
      }

      return false;
    });

    if (!hasRequiredService) {
      return <Error404 isTenant={false} />;
    }
  }

  return <>{children}</>;
};

export default CompanyGuard;
